"use client";

import Link from "next/link";
import { Suspense, useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useInvoiceStore } from "@/lib/invoice/store";
import { storageEstimate } from "@/lib/invoice/idb";
import { passwordProblems } from "@/lib/invoice/crypto";
import { DEFAULT_BRAND } from "@/lib/invoice/defaults";
import { TEMPLATES } from "@/lib/invoice/templates";
import { LETTERHEADS } from "@/lib/invoice/letters";
import { FEATURES, INDUSTRY_PRESETS } from "@/lib/invoice/presets";
import GoogleButton from "@/components/Invoice/GoogleButton";
import { checkIn, getAccount, signInWithGoogle, signOutAccount } from "@/lib/invoice/account";
import { FORMATS, downloadBlob, financialYear, formatNumber, todayISO, uid } from "@/lib/invoice/utils";
import { Button, Card, Combobox, Field, ImageUpload, Input, Loading, Modal, PageHeader, Select, Tabs, Textarea, removeWhiteBackground, useFeedback } from "@/components/Invoice/ui";
import { currencyOptions, taxModeOptions } from "@/components/Invoice/options";
import { AddressFields } from "@/components/Invoice/AddressFields";
import { DEFAULT_APP_NAME } from "@/lib/invoice/appName";
import { PRORATE_OPTIONS } from "@/lib/invoice/billing";
import { AI_MODELS, askGemini } from "@/lib/invoice/ai";
import { findCountry } from "@/lib/invoice/geo";
import TemplatePicker from "@/components/Invoice/TemplatePicker";
import { AiDot, useAiState } from "@/components/Invoice/AskAI";
import { THEMES, getTheme, setTheme } from "@/lib/invoice/theme";
import { useDrive } from "@/components/Invoice/DriveSync";

const TABS = [
  { value: "general", label: "General" },
  { value: "brands", label: "Business profiles" },
  { value: "numbering", label: "Numbering" },
  { value: "defaults", label: "Defaults" },
  { value: "data", label: "Data & backup" },
  { value: "security", label: "Security & login" },
  { value: "tags", label: "Tags" },
  { value: "ai", label: "AI (Gemini)" },
  { value: "trash", label: "Recycle bin" },
];

// Look of the app on this device – applies instantly, no Save needed.
function AppearanceCard() {
  const [theme, setT] = useState("glass");
  useEffect(() => setT(getTheme()), []);
  return (
    <Card title="Appearance" subtitle="How the app looks on this device" className="xl:col-span-2">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Appearance">
        {THEMES.map((t) => (
          <button
            key={t.value}
            type="button"
            role="radio"
            aria-checked={theme === t.value}
            data-testid={`theme-${t.value}`}
            onClick={() => {
              setTheme(t.value);
              setT(t.value);
            }}
            className={`flex items-center gap-4 rounded-xl border p-3 text-left transition ${theme === t.value ? "border-brand-500 ring-2 ring-brand-500/30" : "border-slate-200 hover:border-slate-300"}`}
          >
            <span
              aria-hidden
              className="relative h-14 w-20 shrink-0 overflow-hidden rounded-lg ring-1 ring-black/5"
              style={{
                background:
                  t.value === "glass"
                    ? "radial-gradient(circle at 20% 20%, #7dd3fc, transparent 60%), radial-gradient(circle at 85% 30%, #c4b5fd, transparent 60%), radial-gradient(circle at 60% 100%, #fca5a5, transparent 60%), #eef1f8"
                    : "#f6f7f9",
              }}
            >
              <span
                className="absolute inset-x-2 bottom-2 top-4 rounded-md"
                style={t.value === "glass" ? { background: "rgba(255,255,255,.5)", backdropFilter: "blur(6px)", boxShadow: "inset 0 1px 0 #fff, inset 0 0 0 1px rgba(255,255,255,.6)" } : { background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,.08)", border: "1px solid #e2e8f0" }}
              />
            </span>
            <span>
              <span className="block text-sm font-semibold text-slate-900">{t.label}</span>
              <span className="block text-xs text-slate-500">{t.hint}</span>
            </span>
          </button>
        ))}
      </div>
    </Card>
  );
}

function AiPanel({ s, setS, save }) {
  const { toast } = useFeedback();
  const [testing, setTesting] = useState(false);
  const ai = s.ai || {};
  const set = (patch) => setS((x) => ({ ...x, ai: { ...(x.ai || {}), ...patch } }));
  const status = useAiState();
  const test = async () => {
    setTesting(true);
    try {
      const r = await askGemini({ ...s, ai: { ...ai, enabled: true } }, { prompt: "Reply with the single word: connected" });
      toast(`Gemini answered: “${r.slice(0, 40)}” – it works`, { tone: "success" });
    } catch (e) {
      toast(e.message, { tone: "error" });
    } finally {
      setTesting(false);
    }
  };
  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
      <Card title="Google Gemini" subtitle="Optional – bring your own API key" actions={<Button onClick={save}>Save</Button>}>
        <div className="space-y-3">
          <div className={status.state === "on" ? "flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 ring-1 ring-emerald-200" : "flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-800 ring-1 ring-red-200"} data-testid="ai-settings-status">
            <AiDot state={status.state} />
            {status.state === "on" ? `Connected – ${status.model}` : status.state === "error" ? status.error : "Not connected – paste your key, Save, then Test connection"}
          </div>
          <Field label="Gemini API key" hint={<>Create one free at <a className="text-brand-700 underline" href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer">aistudio.google.com/apikey</a>, then paste it here.</>}>
            <Input type="password" autoComplete="off" value={ai.apiKey || ""} onChange={(e) => set({ apiKey: e.target.value.trim() })} placeholder="AIza…" data-testid="ai-key" />
          </Field>
          <Field label="Model">
            <Combobox value={ai.model || AI_MODELS[0].value} onChange={(model) => set({ model })} options={AI_MODELS} />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={ai.enabled !== false} onChange={(e) => set({ enabled: e.target.checked })} /> Turn on AI features
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={ai.importHelp !== false} onChange={(e) => set({ importHelp: e.target.checked })} /> Use AI for invoices the importer can’t read cleanly
          </label>
          <Button variant="secondary" disabled={!ai.apiKey || testing} onClick={test}>{testing ? "Testing…" : "Test connection"}</Button>
        </div>
      </Card>
      <Card title="What AI does here">
        <ul className="list-disc space-y-2 pl-5 text-sm text-slate-600">
          <li><b>Ask AI</b> (top bar) – questions about your clients, invoices, projects and sales, e.g. “Who owes me money?”.</li>
          <li><b>Payment reminders</b> – writes a friendly or firm reminder email from the invoice.</li>
          <li><b>Import help</b> – re-reads invoices the built-in reader couldn’t match exactly.</li>
        </ul>
        <div className="mt-4 rounded-lg bg-amber-50 p-3 text-xs leading-relaxed text-amber-900 ring-1 ring-amber-200">
          Privacy: everything else stays on your device and your Google Drive. Only when you use an AI feature is the data needed for that request (a summary of your figures, the invoice text or the reminder details – never bank details or passwords) sent to Google’s Gemini API under your own key. The key is saved with your data and encrypted when login is on.
        </div>
      </Card>
    </div>
  );
}

// "Remove white background" for scanned stamps and signatures.
function MakeTransparent({ value, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={busy}
      className="mt-1.5 text-[11px] font-medium text-brand-700 hover:underline disabled:text-slate-400"
      onClick={async () => {
        setBusy(true);
        try {
          onChange(await removeWhiteBackground(value));
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? "Working…" : "Remove white background"}
    </button>
  );
}

// Pixel size of the stored image, with a nudge when it is too small to print sharply.
function StampSize({ src }) {
  const [size, setSize] = useState(null);
  useEffect(() => {
    const img = new Image();
    img.onload = () => setSize([img.width, img.height]);
    img.src = src;
  }, [src]);
  if (!size) return null;
  const small = Math.max(...size) < 500;
  return (
    <div className={`mt-1 text-[11px] ${small ? "text-amber-700" : "text-emerald-700"}`} data-testid="stamp-size">
      {size[0]} × {size[1]} px{small ? " – low resolution, may print blurry. Upload a larger scan." : " – HD"}
    </div>
  );
}

function BrandEditor({ value, onChange, store }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const setBank = (sec, k) => (e) => onChange({ ...value, [sec]: { ...value[sec], [k]: e.target.value } });
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Business name *" className="sm:col-span-2"><Input value={value.name} onChange={set("name")} /></Field>
        <Field label="Tagline (optional)" className="sm:col-span-2"><Input value={value.tagline} onChange={set("tagline")} /></Field>
        <Field
          label="Business type"
          className="sm:col-span-2"
          hint={
            value.industry
              ? `New quotations start with: ${(INDUSTRY_PRESETS.find((p) => p.id === value.industry)?.features || []).map((f) => FEATURES[f].label.toLowerCase()).join(", ") || "a plain item list"}. Other options stay hidden (you can still switch them on per document).`
              : "Picks the quotation templates, terms and document options (sections, optional items, payment schedule…) that fit your business"
          }
        >
          <Select value={value.industry || ""} onChange={set("industry")} data-testid="brand-industry">
            <option value="">— Not set (show everything) —</option>
            {INDUSTRY_PRESETS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.icon} {p.label}
              </option>
            ))}
          </Select>
        </Field>
        <AddressFields value={{ ...value, country: value.country ?? "India" }} onChange={onChange} stateLabel={findCountry(value.country || "India")?.[0] === "IN" ? "State (decides CGST+SGST vs IGST)" : undefined} />
        <Field label="Tax ID (GSTIN / VAT no.)"><Input value={value.gstin} onChange={set("gstin")} /></Field>
        <Field label="PAN"><Input value={value.pan} onChange={set("pan")} /></Field>
        <Field label="LUT / ARN (export invoices)"><Input value={value.lut} onChange={set("lut")} /></Field>
        <Field label="Other registration" hint="Shown on invoices and letterhead, e.g. MSME / Udyam, CIN, Company No., Trade licence">
          <div className="flex gap-1">
            <Input className="w-2/5" value={value.regLabel ?? ""} onChange={set("regLabel")} placeholder="MSME" />
            <Input className="w-3/5" value={value.regNo ?? ""} onChange={set("regNo")} placeholder="UDYAM-XX-00-0000000" />
          </div>
        </Field>
        <Field label="Email"><Input value={value.email} onChange={set("email")} /></Field>
        <Field label="Phone"><Input value={value.phone} onChange={set("phone")} /></Field>
        <Field label="Website"><Input value={value.website} onChange={set("website")} /></Field>
        <Field label="Invoice number prefix" hint="Blank = global prefix. Use a different prefix per GSTIN / entity.">
          <Input value={value.prefix} onChange={set("prefix")} placeholder={store.settings.numbering.prefix} />
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Logo"><ImageUpload value={value.logo} onChange={(logo) => onChange({ ...value, logo })} /></Field>
        <Field label="Signature" hint="Kept in HD (up to 1600 px). A PNG with a transparent background works best.">
          <ImageUpload wide max={1600} value={value.signature} onChange={(signature) => onChange({ ...value, signature })} />
          {value.signature && <MakeTransparent value={value.signature} onChange={(signature) => onChange({ ...value, signature })} testId="signature-transparent" />}
        </Field>
        <Field label="Signatory label"><Input value={value.signatory} onChange={set("signatory")} /></Field>
      </div>

      <div className="rounded-xl border border-slate-200 p-4" data-testid="stamp-settings">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <div className="text-sm font-semibold text-slate-800">Company stamp / seal</div>
          <div className="text-[11px] text-slate-500">Kept in HD (up to 1600 px) so it prints sharp. Scan or photograph it at 600 × 600 px or more.</div>
        </div>
        <div className="flex flex-wrap items-start gap-6">
          <div>
            <ImageUpload tall max={1600} label={value.stamp ? "Replace stamp" : "Upload stamp"} value={value.stamp} onChange={(stamp) => onChange({ ...value, stamp })} />
            {value.stamp && <MakeTransparent value={value.stamp} onChange={(stamp) => onChange({ ...value, stamp })} testId="stamp-transparent" />}
            {value.stamp && <StampSize src={value.stamp} />}
          </div>
          <div className="space-y-1.5 text-sm text-slate-700">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={value.stampOnDocs !== false} disabled={!value.stamp} onChange={(e) => onChange({ ...value, stampOnDocs: e.target.checked })} data-testid="stamp-on-docs" />
              Show on invoices, quotations and other documents
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={value.stampOnLetters !== false} disabled={!value.stamp} onChange={(e) => onChange({ ...value, stampOnLetters: e.target.checked })} />
              Show on letters (can be switched off per letter)
            </label>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Brand colour">
          <div className="flex gap-2">
            <input type="color" value={value.accent} onChange={set("accent")} className="h-9 w-12 rounded border" />
            <Input value={value.accent} onChange={set("accent")} />
          </div>
          <div className="mt-2 flex gap-1">
            {["#C62828", "#1D4ED8", "#047857", "#7C3AED", "#0F172A", "#EA580C", "#0E7490", "#BE185D"].map((c) => (
              <button key={c} title={c} className="h-5 w-5 rounded-full border border-white shadow ring-1 ring-slate-200" style={{ background: c }} onClick={() => onChange({ ...value, accent: c })} />
            ))}
          </div>
        </Field>
      </div>

      <div>
        <div className="mb-2 text-xs font-medium text-slate-600">Default template for this profile</div>
        <TemplatePicker value={value.template} onChange={(template) => onChange({ ...value, template })} data={{ ...store, brands: [value], settings: { ...store.settings, defaultBrandId: value.id } }} />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Default letterhead design" hint="Used for letters on letterhead – see the Letterhead page for previews and blank letterhead PDFs">
          <Select value={value.letterhead || "classic"} onChange={set("letterhead")}>
            {Object.entries(LETTERHEADS).map(([k, t]) => (
              <option key={k} value={k}>
                {t.label} ({t.tier})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="QR code on letters (link)" hint="e.g. your website, Google review page or catalogue – printed on letters">
          <Input value={value.qrLink || ""} onChange={set("qrLink")} placeholder="https://g.page/r/…" />
        </Field>
        <Field label="Text under the QR">
          <Input value={value.qrLabel || ""} onChange={set("qrLabel")} placeholder="Scan to review us" />
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div>
          <div className="mb-2 text-sm font-semibold">Bank – international (GBP / USD / EUR…)</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              ["accountName", "Account holder"],
              ["method", "Payment method"],
              ["currency", "Account currency"],
              ["accountNumber", "Account number"],
              ["sortCode", "Sort code"],
              ["iban", "IBAN"],
              ["swift", "SWIFT / BIC"],
              ["bankName", "Bank name"],
            ].map(([k, l]) => (
              <Field key={k} label={l}><Input value={value.bank[k] || ""} onChange={setBank("bank", k)} /></Field>
            ))}
            <Field label="Beneficiary / bank address" className="sm:col-span-2"><Input value={value.bank.bankAddress || ""} onChange={setBank("bank", "bankAddress")} /></Field>
          </div>
        </div>
        <div>
          <div className="mb-2 text-sm font-semibold">Bank – India (INR invoices)</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              ["accountName", "Account holder"],
              ["accountNumber", "Account number"],
              ["ifsc", "IFSC"],
              ["bankName", "Bank name"],
              ["branch", "Branch"],
              ["upiId", "UPI ID (for pay QR)"],
            ].map(([k, l]) => (
              <Field key={k} label={l}><Input value={value.bankInr[k] || ""} onChange={setBank("bankInr", k)} /></Field>
            ))}
          </div>
          <div className="mt-2 text-xs text-slate-500">If empty, INR invoices show the international bank details.</div>
        </div>
      </div>
    </div>
  );
}

function DriveCard() {
  const drive = useDrive();
  const [clientId, setClientId] = useState(drive.cfg?.clientId || "");
  const [help, setHelp] = useState(false);
  const cfg = drive.cfg;
  if (!cfg) return null;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return (
    <Card title="Google Drive sync (all devices & Chrome profiles)" className="xl:col-span-2">
      {cfg.enabled ? (
        <div className="space-y-2 text-sm">
          <div>
            Syncing with Google Drive{cfg.email ? <> as <b>{cfg.email}</b></> : ""} · file <code className="rounded bg-slate-100 px-1">invoice-studio-data.json</code>
          </div>
          <div className="text-slate-600">
            Status: <b>{{ "needs-auth": "signed out – sign in to sync", syncing: "syncing…", synced: cfg.dirty ? "saving changes…" : "up to date", conflict: "waiting for your choice", error: drive.error, off: "off" }[drive.status]}</b>
            {cfg.lastSyncAt && <> · last synced {new Date(cfg.lastSyncAt).toLocaleString()}</>}
          </div>
          <div className="flex flex-wrap gap-2">
            {drive.status === "needs-auth" ? <Button size="sm" onClick={() => drive.reconnect()}>Sign in to Google</Button> : <Button size="sm" onClick={() => drive.syncNow()}>Sync now</Button>}
            <Button size="sm" variant="ghost" onClick={() => drive.disconnect()}>Turn off on this device</Button>
          </div>
          <div className="text-xs text-slate-500">
            Open this tool in any other Chrome profile, browser or computer, go to Settings → Data & backup and connect the same Google account – the latest data loads
            automatically. Changes save to Drive a few seconds after you make them. Google asks you to sign in again about once an hour; data keeps saving on this device
            meanwhile and syncs when you sign in.
          </div>
        </div>
      ) : (
        <div className="space-y-3 text-sm text-slate-600">
          <div>
            Keep your invoices in <b>your own Google Drive</b> so every Chrome profile, laptop or phone shows the same data. The app can only see the one file it creates –
            nothing else in your Drive.
          </div>
          {!drive.envClientId && (
            <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
              <Field label="Google OAuth Client ID" hint={<>One-time setup (5 minutes). <button className="text-brand-700 underline" onClick={() => setHelp((h) => !h)}>{help ? "Hide steps" : "How do I get this?"}</button> · <Link className="text-brand-700 underline" href="/invoice/help#drive">Step-by-step guide with pictures</Link></>}>
                <Input value={clientId} onChange={(e) => setClientId(e.target.value.trim())} placeholder="1234567890-abc123.apps.googleusercontent.com" />
              </Field>
            </div>
          )}
          {help && (
            <ol className="list-decimal space-y-1 rounded-lg bg-slate-50 py-3 pl-8 pr-4 text-xs text-slate-700">
              <li>Go to <b>console.cloud.google.com</b> → create a project (e.g. “Invoice Studio”).</li>
              <li>APIs & Services → Library → enable <b>Google Drive API</b>.</li>
              <li>OAuth consent screen → External → add your email as a test user (or publish).</li>
              <li>Credentials → Create credentials → <b>OAuth client ID</b> → Web application.</li>
              <li>Authorised JavaScript origins: add <code className="rounded bg-white px-1">{origin}</code> (and any other address you open the tool on).</li>
              <li>Copy the Client ID here. Tip: set it as <code className="rounded bg-white px-1">NEXT_PUBLIC_GOOGLE_CLIENT_ID</code> when deploying so no one has to paste it.</li>
            </ol>
          )}
          <Button
            onClick={() => {
              const id = drive.envClientId || clientId;
              if (!id) return;
              drive.setClientId(id);
              drive.connect(id);
            }}
            disabled={!drive.envClientId && !clientId}
          >
            Connect Google Drive
          </Button>
        </div>
      )}
    </Card>
  );
}

// Your account on the account server: plan, expiry, sign in / out.
function AccountCard({ store }) {
  const { toast } = useFeedback();
  const [acc, setAcc] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setAcc(getAccount());
    checkIn(store, { force: true }).then(setAcc);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const a = acc?.account;
  const date = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
  return (
    <Card title="Your account" subtitle="Plan, renewals and updates. Your invoices are never sent to our server." className="xl:col-span-2" data-testid="account-card">
      {a ? (
        <div className="flex flex-wrap items-center gap-4">
          {a.picture ? <img src={a.picture} alt="" className="h-11 w-11 rounded-full" referrerPolicy="no-referrer" /> : <div className="h-11 w-11 rounded-full bg-slate-200" />}
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900">{a.name || a.email}</div>
            <div className="text-sm text-slate-500">{a.email}</div>
          </div>
          <div className="text-sm">
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${a.expired ? "bg-amber-100 text-amber-800" : a.plan === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"}`}>
              {a.status === "disabled" ? "Disabled" : a.expired ? "Expired" : a.plan === "paid" ? "Paid" : a.plan === "trial" ? "Trial" : "Free"}
            </span>
            <span className="ml-2 text-slate-600">{a.expiresAt ? `${a.expired ? "ended" : "valid until"} ${date(a.expiresAt)}` : ""}</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              signOutAccount();
              setAcc(null);
              toast("Signed out of your account – your data stays on this device");
            }}
          >
            Sign out
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-4">
          <div className="min-w-[240px] flex-1 text-sm text-slate-600">Sign in with Google to activate your plan, get renewal reminders and support. {store.loginEnabled ? "" : "Turn on “Login” in Security to also encrypt your data with this account."}</div>
          <GoogleButton
            onCredential={async (credential) => {
              setBusy(true);
              try {
                const r = await signInWithGoogle(credential, store);
                setAcc(getAccount());
                toast(`Signed in as ${r.account.email}`, { tone: "success" });
              } catch (e) {
                toast(e.message, { tone: "error" });
              } finally {
                setBusy(false);
              }
            }}
          />
          {busy && <span className="text-sm text-slate-500">Signing in…</span>}
        </div>
      )}
    </Card>
  );
}

function SecurityPanel({ store }) {
  const { toast, confirm } = useFeedback();
  const [f, setF] = useState({ username: "", password: "", confirm: "", me: store.currentUser || "", myPassword: "", newUser: "", newPassword: "", oldPassword: "", nextPassword: "" });
  const [busy, setBusy] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      await fn();
      toast(ok, { tone: "success" });
      setF((x) => ({ ...x, password: "", confirm: "", myPassword: "", newUser: "", newPassword: "", oldPassword: "", nextPassword: "" }));
    } catch (e) {
      toast(e.message, { tone: "error" });
    } finally {
      setBusy("");
    }
  };
  const pwIssue = (pw) => passwordProblems(pw);

  if (!store.loginEnabled) {
    const issue = f.password ? pwIssue(f.password) : "";
    return (
      <Card title="Login" subtitle="Off – anyone who opens this app on this device can see your data">
        <div className="mb-5 flex flex-wrap items-center gap-4 rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200" data-testid="google-login-setup">
          <div className="min-w-[220px] flex-1 text-sm text-slate-600">
            <b className="text-slate-900">Easiest: sign in with Google.</b> Your data is encrypted and opens on any device where you sign in with the same Google account – no password to remember.
          </div>
          <GoogleButton
            onCredential={(credential) =>
              run("g", async () => {
                const r = await signInWithGoogle(credential, store);
                await store.enableGoogleLogin({ sub: r.sub, email: r.account.email, vaultKey: r.vaultKey });
              }, "Login with Google is on")
            }
          />
        </div>
        <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Or a user ID &amp; password</div>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="space-y-3 text-sm text-slate-600">
            <p className="text-sm">Turning on login does two things:</p>
            <ul className="list-disc space-y-1 pl-5 text-sm">
              <li>A sign-in screen appears whenever the app is opened (you can tick “remember this device”).</li>
              <li>
                <b>All your data is encrypted</b> with your password – in this browser, in the Google Drive file and in backups. Without a valid user ID and password it
                can’t be read, even by someone who has the files or the site link.
              </li>
            </ul>
            <div className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-inset ring-amber-200">
              <b>There is no “forgot password”.</b> Nobody – not even a developer – can recover encrypted data without a password. Write your password down somewhere safe,
              and add a second user (e.g. a partner) as a spare key.
            </div>
          </div>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!f.username.trim()) return toast("Choose a user ID", { tone: "error" });
              if (pwIssue(f.password)) return toast(pwIssue(f.password), { tone: "error" });
              if (f.password !== f.confirm) return toast("Passwords don’t match", { tone: "error" });
              run("enable", () => store.enableLogin(f.username.trim(), f.password), "Login is on – your data is now encrypted");
            }}
          >
            <Field label="User ID"><Input value={f.username} onChange={set("username")} autoComplete="username" placeholder="e.g. himanshu" /></Field>
            <Field label="Password" hint={issue || "At least 8 characters, mixing letters with numbers or symbols."}>
              <Input type="password" value={f.password} onChange={set("password")} autoComplete="new-password" />
            </Field>
            <Field label="Confirm password"><Input type="password" value={f.confirm} onChange={set("confirm")} autoComplete="new-password" /></Field>
            <Button type="submit" disabled={busy === "enable"}>{busy === "enable" ? "Encrypting…" : "Turn on login"}</Button>
          </form>
        </div>
      </Card>
    );
  }

  const auth = store.auth;
  const meGoogle = auth.users.find((u) => u.username === store.currentUser)?.kind === "google";
  const meArg = meGoogle ? null : store.currentUser || f.me; // Google users don't need a password here
  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
      <Card title="Login is on" subtitle={`Signed in as ${store.currentUser || "—"} · data encrypted with AES-256`} actions={<Button variant="dark" size="sm" onClick={() => store.lock()}>Lock now</Button>}>
        <ul className="divide-y divide-slate-100 text-sm">
          {auth.users.map((u) => (
            <li key={u.username} className="flex items-center justify-between py-2.5">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold uppercase text-slate-600">{u.username.slice(0, 2)}</div>
                <div>
                  <div className="font-medium">
                    {u.username}
                    {u.kind === "google" && <span className="ml-2 rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-sky-700">Google</span>}
                    {u.username === store.currentUser && <span className="ml-2 text-xs text-emerald-600">you</span>}
                  </div>
                  <div className="text-xs text-slate-500">added {new Date(u.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
              {auth.users.length > 1 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600"
                  onClick={async () => {
                    if (!meGoogle && !f.myPassword) return toast("Enter your own password in “Confirm it’s you” first", { tone: "error" });
                    if (!(await confirm(`Remove user “${u.username}”? They won’t be able to sign in any more.`, { okLabel: "Remove", danger: true }))) return;
                    run("rm", () => store.removeUser(meArg, f.myPassword, u.username), "User removed");
                  }}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Auto-lock after inactivity">
            <Select value={auth.autoLockMinutes || 0} onChange={(e) => store.setAutoLock(e.target.value)}>
              <option value={0}>Never</option>
              {[5, 15, 30, 60, 240].map((m) => <option key={m} value={m}>{m < 60 ? `${m} minutes` : `${m / 60} hour${m > 60 ? "s" : ""}`}</option>)}
            </Select>
          </Field>
        </div>
      </Card>

      <Card title="Sign in with Google" subtitle="Add a Google account that can open this workspace on any device">
        <div className="space-y-3 text-sm text-slate-600">
          <p>The person signs in with Google below (on this device, now). From then on they can open the data on any computer or phone with that Google account – no password.</p>
          <GoogleButton
            text="signin_with"
            onCredential={(credential) =>
              run("g", async () => {
                const r = await signInWithGoogle(credential, store);
                await store.enableGoogleLogin({ sub: r.sub, email: r.account.email, vaultKey: r.vaultKey });
              }, "Google sign-in added")
            }
          />
        </div>
      </Card>

      <Card title={meGoogle ? "Add a user ID & password" : "Confirm it’s you"} subtitle={meGoogle ? "A spare way in that doesn’t need Google" : "Needed to add or remove users"}>
        {!meGoogle && <Field label={`Your password (${store.currentUser || "current user"})`}><Input type="password" value={f.myPassword} onChange={set("myPassword")} autoComplete="current-password" /></Field>}
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="New user ID"><Input value={f.newUser} onChange={set("newUser")} /></Field>
          <Field label="Their password" hint={f.newPassword ? pwIssue(f.newPassword) : ""}><Input type="password" value={f.newPassword} onChange={set("newPassword")} autoComplete="new-password" /></Field>
        </div>
        <Button
          className="mt-3"
          disabled={busy === "add"}
          onClick={() => {
            if (!meGoogle && !f.myPassword) return toast("Enter your own password first", { tone: "error" });
            if (!f.newUser.trim()) return toast("Choose a user ID", { tone: "error" });
            if (pwIssue(f.newPassword)) return toast(pwIssue(f.newPassword), { tone: "error" });
            run("add", () => store.addUser(meArg, f.myPassword, f.newUser.trim(), f.newPassword), `User “${f.newUser.trim()}” added`);
          }}
        >
          Add user
        </Button>
      </Card>

      {!meGoogle && <Card title="Change your password">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Current password"><Input type="password" value={f.oldPassword} onChange={set("oldPassword")} autoComplete="current-password" /></Field>
          <Field label="New password" hint={f.nextPassword ? pwIssue(f.nextPassword) : ""}><Input type="password" value={f.nextPassword} onChange={set("nextPassword")} autoComplete="new-password" /></Field>
        </div>
        <Button
          className="mt-3"
          variant="secondary"
          disabled={busy === "pw"}
          onClick={() => {
            if (pwIssue(f.nextPassword)) return toast(pwIssue(f.nextPassword), { tone: "error" });
            run("pw", () => store.changePassword(store.currentUser, f.oldPassword, f.nextPassword), "Password changed");
          }}
        >
          Change password
        </Button>
        <div className="mt-2 text-xs text-slate-500">Other devices pick up the new password after the next Google Drive sync.</div>
      </Card>}

      <Card title="Turn off login">
        <div className="text-sm text-slate-600">Removes the sign-in screen and stores data unencrypted again on this device (and in Drive/backups from now on).</div>
        <Button
          className="mt-3"
          variant="danger"
          onClick={async () => {
            if (!meGoogle && !f.oldPassword) return toast("Enter your current password in “Change your password” first", { tone: "error" });
            if (!(await confirm("Turn off login and store data unencrypted?", { okLabel: "Turn off", danger: true }))) return;
            run("off", () => store.disableLogin(meGoogle ? null : store.currentUser, f.oldPassword), "Login turned off");
          }}
        >
          Turn off login
        </Button>
      </Card>
    </div>
  );
}

function TagsManager({ store }) {
  const { toast, confirm } = useFeedback();
  const [tags, setTags] = useState(() => structuredClone(store.settings.tags));
  const usage = (id) =>
    store.projects.filter((p) => p.tag === id).length +
    store.invoices.filter((i) => (i.tags || []).includes(id) || (i.items || []).some((l) => l.tag === id)).length;
  const set = (i, patch) => setTags((t) => t.map((x, k) => (k === i ? { ...x, ...patch } : x)));
  return (
    <Card
      title="Tags / service categories"
      actions={
        <>
          <Button variant="secondary" onClick={() => setTags((t) => [...t, { id: uid(), name: "", color: "#64748b" }])}>+ Add tag</Button>
          <Button
            onClick={() => {
              const clean = tags.filter((t) => t.name.trim()).map((t) => ({ ...t, name: t.name.trim() }));
              store.updateSettings({ tags: clean });
              setTags(clean);
              toast("Tags saved", { tone: "success" });
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="mb-3 text-sm text-slate-600">
        Tag projects and invoice lines by service (SEO, Website, Ads…). Invoices pick up their lines’ tags automatically, and you can filter invoices
        and see “Revenue by service” in Reports.
      </div>
      <div className="space-y-2">
        {tags.map((t, i) => (
          <div key={t.id} className="flex flex-wrap items-center gap-2">
            <input type="color" value={t.color} onChange={(e) => set(i, { color: e.target.value })} className="h-9 w-10 rounded border" />
            <Input className="max-w-xs" value={t.name} placeholder="Tag name" onChange={(e) => set(i, { name: e.target.value })} />
            <span className="text-xs text-slate-500">{usage(t.id)} use(s)</span>
            <Button
              size="sm"
              variant="ghost"
              className="text-brand-600"
              onClick={async () => {
                const n = usage(t.id);
                if (n && !(await confirm(`“${t.name}” is used ${n} time(s). Remove it? Items keep working but lose this tag.`, { okLabel: "Remove", danger: true }))) return;
                setTags((x) => x.filter((_, k) => k !== i));
              }}
            >
              Remove
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function SettingsInner() {
  const store = useInvoiceStore();
  const params = useSearchParams();
  const { toast, confirm } = useFeedback();
  const [tab, setTab] = useState(params.get("tab") || "general");
  const [s, setS] = useState(null);
  const [brand, setBrand] = useState(null);
  const [usage, setUsage] = useState(null);
  const [locked, setLocked] = useState(null); // protected backup waiting for a login

  useEffect(() => {
    if (store.ready && !s) setS(structuredClone(store.settings));
  }, [store.ready, store.settings, s]);
  useEffect(() => {
    if (tab === "data") storageEstimate().then(setUsage);
  }, [tab, store.invoices.length]);

  if (!store.ready || !s) return <Loading />;

  const fy = financialYear(todayISO());
  const defBrand = store.brandById(s.defaultBrandId);
  const key = `${defBrand?.prefix || s.numbering.prefix}|${s.numbering.resetEachFY === false ? "all" : fy}`;
  const nextNo = s.numbering.counters?.[key] ?? s.numbering.start ?? 1;
  const setSec = (sec, k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setS((x) => ({ ...x, [sec]: { ...x[sec], [k]: v } }));
  };
  const inp = (sec, k, props = {}) => <Input value={s[sec][k] ?? ""} onChange={setSec(sec, k)} {...props} />;

  const saveSettings = () => {
    store.updateSettings({
      ...s,
      numbering: { ...s.numbering, padding: Number(s.numbering.padding) || 0, start: Number(s.numbering.start) || 1 },
      defaults: { ...s.defaults, taxRate: Number(s.defaults.taxRate) || 0, dueDays: Number(s.defaults.dueDays) || 0 },
    });
    toast("Settings saved", { tone: "success" });
  };

  const saveBrand = () => {
    if (!brand.name.trim()) return toast("Business name is required", { tone: "error" });
    store.saveBrand(brand);
    setBrand(null);
    toast("Business profile saved", { tone: "success" });
  };

  const exportBackup = async () => {
    const payload = await store.exportBackup();
    downloadBlob(new Blob([JSON.stringify(payload, null, 1)], { type: "application/json" }), `invoice-backup-${todayISO()}${payload.encrypted ? "-protected" : ""}.json`);
    toast(payload.encrypted ? "Protected backup downloaded – it needs your user ID and password to restore" : "Backup downloaded – keep it in Google Drive / OneDrive", { tone: "success" });
  };

  const restoreParsed = async (obj) => {
    const ok = await confirm(
      `Replace ALL current data with this backup? (${obj.clients?.length || 0} clients, ${obj.projects?.length || 0} projects, ${obj.invoices?.length || 0} invoices${obj.exportedAt ? `, saved ${obj.exportedAt.slice(0, 10)}` : ""})`,
      { okLabel: "Replace data", danger: true }
    );
    if (!ok) return;
    store.importAll(obj);
    setS(null);
    toast("Backup restored", { tone: "success" });
  };

  const importBackup = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    f.text().then(async (txt) => {
      try {
        const json = JSON.parse(txt);
        if (json.encrypted) {
          try {
            return await restoreParsed(await store.openEnvelope(json));
          } catch {
            return setLocked({ env: json, username: "", password: "", error: "" });
          }
        }
        await restoreParsed(json);
      } catch {
        toast("That file is not a valid backup.", { tone: "error" });
      }
    });
  };

  const mb = (n) => `${(n / 1024 / 1024).toFixed(1)} MB`;
  const unlockBackup = async () => {
    try {
      const obj = await store.openEnvelopeWithLogin(locked.env, locked.username, locked.password);
      setLocked(null);
      await restoreParsed(obj);
    } catch (e) {
      setLocked((l) => ({ ...l, error: e.message }));
    }
  };

  return (
    <div className="pb-24">
      <PageHeader title="Settings" subtitle="Branding, numbering, defaults and your data" />
      <Tabs tabs={TABS} value={tab} onChange={setTab} />

      {tab === "general" && (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <AppearanceCard />
          <AccountCard store={store} />
          <Card title="App name" subtitle="Rename this tool – shown in the sidebar, on the sign-in screen and in the browser tab." actions={<Button onClick={saveSettings}>Save</Button>}>
            <div className="space-y-3">
              <Field label="Name" hint={`Leave blank for “${DEFAULT_APP_NAME}”. Your invoices still use your business profile name.`}>
                <Input value={s.appName ?? ""} maxLength={40} placeholder={DEFAULT_APP_NAME} onChange={(e) => setS((x) => ({ ...x, appName: e.target.value }))} data-testid="app-name" />
              </Field>
              <Field label="Your name" hint="Used for the greeting on the dashboard, e.g. “Good morning, Himanshu”">
                <Input value={s.ownerName ?? ""} maxLength={40} onChange={(e) => setS((x) => ({ ...x, ownerName: e.target.value }))} data-testid="owner-name" />
              </Field>
              <Field label="Subtitle under the name" hint="Blank = your default business profile name">
                <Input value={s.appTagline ?? ""} maxLength={60} onChange={(e) => setS((x) => ({ ...x, appTagline: e.target.value }))} />
              </Field>
            </div>
          </Card>
          <Card title="Sign-in" subtitle={store.loginEnabled ? `Login is on · ${store.auth?.users?.length || 1} user(s)` : "Login is off – anyone using this browser can open your data"}>
            <div className="space-y-3 text-sm text-slate-600">
              <p>Protect the workspace with a user ID and password. Your data is then encrypted on this device and on Google Drive, and the sign-in screen appears every time the tool opens.</p>
              <Button variant={store.loginEnabled ? "secondary" : "primary"} onClick={() => setTab("security")}>{store.loginEnabled ? "Manage users & password" : "Set up login"}</Button>
            </div>
          </Card>
        </div>
      )}

      {tab === "brands" && (
        <Card
          title="Business profiles (agency branding)"
          actions={<Button onClick={() => setBrand({ ...structuredClone(DEFAULT_BRAND), id: uid(), name: "", logo: "", signature: "", prefix: "" })}>+ Add profile</Button>}
        >
          <div className="mb-4 text-sm text-slate-600">
            A profile is the business you invoice <b>as</b>: its name, logo, colours, template, bank and number prefix. Add more if you trade under
            another name or entity (e.g. a UK company or a white-label brand). Each client can default to a profile.
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {store.brands.map((b) => (
              <div key={b.id} className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-card">
                <div className="flex items-center gap-3">
                  {b.logo ? <img src={b.logo} alt="" className="h-12 w-12 object-contain" /> : <div className="h-12 w-12 rounded" style={{ background: b.accent }} />}
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold" style={{ color: b.accent }}>{b.name}</div>
                    <div className="text-xs text-slate-500">
                      {TEMPLATES[b.template]?.label} template · prefix {b.prefix || s.numbering.prefix}
                    </div>
                  </div>
                  {s.defaultBrandId === b.id && <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700">Default</span>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setBrand(structuredClone(b))}>Edit</Button>
                  {s.defaultBrandId !== b.id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setS((x) => ({ ...x, defaultBrandId: b.id }));
                        store.updateSettings({ defaultBrandId: b.id });
                        toast(`${b.name} is now the default profile`);
                      }}
                    >
                      Make default
                    </Button>
                  )}
                  {store.brands.length > 1 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-brand-600"
                      onClick={async () => {
                        if (!(await confirm(`Delete profile “${b.name}”?`, { okLabel: "Delete", danger: true }))) return;
                        const err = store.deleteBrand(b.id);
                        toast(err || "Profile deleted", { tone: err ? "error" : "info" });
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "numbering" && (
        <Card title="Invoice numbering" actions={<Button onClick={saveSettings}>Save</Button>}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Global prefix">{inp("numbering", "prefix")}</Field>
            <Field label="Separator">{inp("numbering", "separator")}</Field>
            <Field label="Zero-pad digits" hint="0 = 13, 3 = 013">{inp("numbering", "padding", { type: "number", min: 0, max: 6 })}</Field>
            <Field label={`Next number (FY ${fy}, ${defBrand?.name})`}>
              <Input
                type="number"
                min={1}
                value={nextNo}
                onChange={(e) => setS((x) => ({ ...x, numbering: { ...x.numbering, counters: { ...x.numbering.counters, [key]: Number(e.target.value) || 1 } } }))}
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={s.numbering.includeFY !== false} onChange={setSec("numbering", "includeFY")} /> Include financial year
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={s.numbering.resetEachFY !== false} onChange={setSec("numbering", "resetEachFY")} /> Restart numbering each April
            </label>
          </div>
          <div className="mt-4 grid gap-1 rounded-lg bg-slate-50 px-3 py-2 text-sm">
            {[["invoice", "Tax invoice"], ["proforma", "Proforma"], ["quote", "Quotation"], ["credit", "Credit note"]].map(([t, l]) => (
              <div key={t}>
                {l}: <b>{formatNumber(s, t, todayISO(), t === "invoice" ? nextNo : 1, defBrand)}</b>
              </div>
            ))}
          </div>
          <div className="mt-2 text-xs text-slate-500">Each document type and profile prefix has its own running sequence per financial year, as GST rules require.</div>
        </Card>
      )}

      {tab === "defaults" && (
        <Card title="Defaults for new invoices" actions={<Button onClick={saveSettings}>Save</Button>}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Default format">
              <Select
                value={s.defaults.format}
                onChange={(e) => {
                  const f = FORMATS[e.target.value];
                  setS((x) => ({ ...x, defaults: { ...x.defaults, format: e.target.value, currency: f.currency, taxMode: f.taxMode } }));
                }}
              >
                {Object.entries(FORMATS).map(([k, f]) => <option key={k} value={k}>{f.label}</option>)}
              </Select>
            </Field>
            <Field label="Currency"><Select value={s.defaults.currency} onChange={setSec("defaults", "currency")} options={currencyOptions} /></Field>
            <Field label="Tax treatment"><Select value={s.defaults.taxMode} onChange={setSec("defaults", "taxMode")} options={taxModeOptions} /></Field>
            <Field label="GST rate %">{inp("defaults", "taxRate", { type: "number" })}</Field>
            <Field label="Payment due in (days)" hint="0 = Due date N/A">{inp("defaults", "dueDays", { type: "number", min: 0 })}</Field>
            <Field label="Default SAC code">{inp("defaults", "sac")}</Field>
            <Field label="Date format">
              <Select value={s.defaults.dateStyle} onChange={setSec("defaults", "dateStyle")}>
                <option value="iso">2026-08-31</option>
                <option value="long">31 Aug 2026</option>
              </Select>
            </Field>
            <Field label="Projects starting / ending mid-month" hint="e.g. £180/month from the 16th of a 30-day month = 15 days → £90">
              <Select value={s.defaults.prorate || "30"} onChange={setSec("defaults", "prorate")} options={PRORATE_OPTIONS} data-testid="prorate" />
            </Field>
            <Field label="Line item order" hint="Switches to “your order” automatically the first time you drag a line">
              <Select value={s.defaults.lineOrder || "az"} onChange={setSec("defaults", "lineOrder")} data-testid="line-order">
                <option value="az">A–Z by description</option>
                <option value="manual">Keep my order (drag to arrange)</option>
              </Select>
            </Field>
            <Field label="Backup reminder (days)">
              <Input type="number" min={1} value={s.backup?.remindDays ?? 7} onChange={(e) => setS((x) => ({ ...x, backup: { ...x.backup, remindDays: Number(e.target.value) || 7 } }))} />
            </Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.defaults.showQr} onChange={setSec("defaults", "showQr")} /> Payment QR code</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.defaults.showHsn} onChange={setSec("defaults", "showHsn")} /> HSN/SAC column</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.defaults.showClientLogo} onChange={setSec("defaults", "showClientLogo")} /> Show client logos</label>
            <div />
            <Field label="QR content for non-INR invoices" className="sm:col-span-2 lg:col-span-4" hint="e.g. a Stripe / PayPal / Wise payment link. Placeholders: {amount} {number} {currency}. INR invoices use UPI automatically.">
              {inp("defaults", "qrText", { placeholder: "https://pay.example.com/?ref={number}&amount={amount}" })}
            </Field>
            <Field label="Default notes" className="sm:col-span-2"><Textarea value={s.defaults.notes} onChange={setSec("defaults", "notes")} /></Field>
            <Field label="Default terms & conditions" className="sm:col-span-2"><Textarea value={s.defaults.terms} onChange={setSec("defaults", "terms")} /></Field>
          </div>
        </Card>
      )}

      {tab === "data" && (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <Card title="Where your data lives">
            <div className="space-y-2 text-sm text-slate-700">
              <div>
                Stored in this browser’s database (IndexedDB) on this computer: {store.clients.length} clients, {store.projects.length} projects,{" "}
                {store.invoices.length} documents{usage ? ` · ${mb(usage.usage)} used of ${mb(usage.quota)} available` : ""}.
              </div>
              <div>
                {store.persisted ? "✅ The browser has granted persistent storage, so it won’t clear this data automatically." : "⚠️ The browser may clear this data if the disk gets full. Keep backups."}
              </div>
              <div className="text-slate-500">Clearing browsing data / site data for this website deletes it. Other browsers and devices don’t see it.</div>
            </div>
          </Card>
          <DriveCard />
          <Card title="Automatic backup file">
            {!store.backup.supported ? (
              <div className="text-sm text-slate-600">Automatic file backup works in Chrome and Edge on desktop. In this browser use manual backups below.</div>
            ) : store.backup.linked ? (
              <div className="space-y-2 text-sm">
                <div>
                  Saving every change to <b>{store.backup.name}</b>
                  {store.backup.lastWrite && <> · last saved {new Date(store.backup.lastWrite).toLocaleTimeString()}</>}
                </div>
                {!store.backup.granted && <div className="text-amber-700">Paused – the browser needs your permission again after a restart.</div>}
                <div className="flex gap-2">
                  {!store.backup.granted && <Button size="sm" onClick={() => store.reconnectBackup()}>Resume</Button>}
                  <Button size="sm" variant="ghost" onClick={() => store.unlinkBackup()}>Turn off</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm text-slate-600">
                <div>Pick a file inside your Google Drive, OneDrive or Dropbox folder and every change is saved there automatically – an off-device copy without a server.</div>
                <Button size="sm" onClick={() => store.linkBackupFile().then(() => toast("Auto-backup on", { tone: "success" })).catch((e) => e.name !== "AbortError" && toast(e.message, { tone: "error" }))}>
                  Choose backup file…
                </Button>
              </div>
            )}
          </Card>
          <Card title="Manual backup & restore" className="xl:col-span-2">
            <div className="flex flex-wrap gap-2">
              <Button onClick={exportBackup}>Download backup (.json)</Button>
              <label className="inline-flex cursor-pointer items-center rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium hover:bg-slate-50">
                Restore from backup…
                <input type="file" accept="application/json,.json" className="hidden" onChange={importBackup} />
              </label>
              <Button
                variant="secondary"
                onClick={() => {
                  const c = store.loadSample();
                  toast(`Imported “${c.name}” with 79 monthly projects`, { tone: "success" });
                }}
              >
                Import Aug 2026 client & 79 projects
              </Button>
              <Button
                variant="danger"
                onClick={async () => {
                  if (!(await confirm("Erase ALL clients, projects, invoices, profiles and settings from this browser? Download a backup first.", { okLabel: "Erase everything", danger: true }))) return;
                  store.resetAll();
                  setS(null);
                  toast("All data erased");
                }}
              >
                Erase everything
              </Button>
            </div>
            {store.settings.backup?.lastExportAt && <div className="mt-2 text-xs text-slate-500">Last manual backup: {new Date(store.settings.backup.lastExportAt).toLocaleString()}</div>}
          </Card>
        </div>
      )}

      {tab === "tags" && <TagsManager store={store} />}
      {tab === "ai" && <AiPanel s={s} setS={setS} save={saveSettings} />}
      {tab === "security" && <SecurityPanel store={store} />}

      {tab === "trash" && (
        <Card
          title="Recycle bin"
          actions={
            store.trash.length > 0 && (
              <Button size="sm" variant="danger" onClick={async () => (await confirm("Permanently delete everything in the recycle bin?", { okLabel: "Empty bin", danger: true })) && store.purgeTrash()}>
                Empty bin
              </Button>
            )
          }
        >
          <div className="mb-3 text-xs text-slate-500">Deleted clients (with their projects), projects and invoices stay here for 30 days.</div>
          {store.trash.length === 0 ? (
            <div className="text-sm text-slate-500">The recycle bin is empty.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {store.trash.map((t) => (
                <li key={t.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                  <div>
                    <div className="font-medium">{t.label}</div>
                    <div className="text-xs capitalize text-slate-500">
                      {t.kind}
                      {t.kind === "client" && t.related?.length ? ` + ${t.related.length} projects` : ""} · deleted {new Date(t.deletedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" onClick={() => { store.restoreTrash(t.id); toast("Restored", { tone: "success" }); }}>Restore</Button>
                    <Button size="sm" variant="ghost" onClick={() => store.purgeTrash(t.id)}>Delete forever</Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <Modal
        open={!!locked}
        onClose={() => setLocked(null)}
        title="This backup is password-protected"
        footer={
          <>
            <Button variant="secondary" onClick={() => setLocked(null)}>Cancel</Button>
            <Button onClick={unlockBackup}>Unlock & restore</Button>
          </>
        }
      >
        {locked && (
          <div className="space-y-3 text-sm">
            <div className="text-slate-600">Enter a user ID and password that were valid when this backup was made. Those logins will be restored too.</div>
            <Input placeholder="User ID" value={locked.username} onChange={(e) => setLocked({ ...locked, username: e.target.value })} />
            <Input placeholder="Password" type="password" value={locked.password} onChange={(e) => setLocked({ ...locked, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && unlockBackup()} />
            {locked.error && <div className="text-red-600">{locked.error}</div>}
          </div>
        )}
      </Modal>

      <Modal
        open={!!brand}
        onClose={() => setBrand(null)}
        title={store.brands.some((b) => b.id === brand?.id) ? "Edit business profile" : "New business profile"}
        xwide
        footer={
          <>
            <Button variant="secondary" onClick={() => setBrand(null)}>Cancel</Button>
            <Button onClick={saveBrand}>Save profile</Button>
          </>
        }
      >
        {brand && <BrandEditor value={brand} onChange={setBrand} store={store} />}
      </Modal>
    </div>
  );
}

export default function SettingsPage() {
  return (
    <Suspense fallback={<Loading />}>
      <SettingsInner />
    </Suspense>
  );
}
